import { logger } from '@vagabondinternet/logging';
import { authErrorCode, authFailure } from './auth-errors';
import { loadEmailAuth } from './load-email-auth';

export interface PasswordResetOptions {
  /** Where Firebase's hosted reset page continues to: id.<domain>/sign-in, keeping a valid return_to. */
  continueUrl: string;
}

/** Identity Platform's password reset; each call rejects with the Firebase error. */
export interface PasswordReset {
  /** Emails a reset link to `email`. With email enumeration protection, an unknown address resolves too. */
  sendResetEmail: (email: string, options: PasswordResetOptions) => Promise<void>;
  /** Checks a reset link's oobCode without using it up, resolving the account's email address. */
  checkResetCode: (oobCode: string) => Promise<string>;
  /** Sets the new password, using up the oobCode. */
  confirmReset: (oobCode: string, password: string) => Promise<void>;
}

/** Identity Platform password reset (email-auth.ts), loading the Firebase SDK on first use. */
export const identityPlatformPasswordReset: PasswordReset = {
  sendResetEmail: async (email, options) => (await loadEmailAuth()).passwordReset.sendResetEmail(email, options),
  checkResetCode: async (oobCode) => (await loadEmailAuth()).passwordReset.checkResetCode(oobCode),
  confirmReset: async (oobCode, password) => (await loadEmailAuth()).passwordReset.confirmReset(oobCode, password),
};

/** What a reset link turned out to be when it can't be used. */
export type PasswordResetLinkFailure = 'expired' | 'invalid';

/**
 * What a failed verifyPasswordResetCode or confirmPasswordReset means for the link. A disabled or deleted account
 * is invalid like a bad code, so nothing is revealed about the account. Network and rate-limit failures are
 * undefined: the link may still be good, so the player can try again. Policy failures are undefined too: they are
 * about the password, not the link.
 */
export const passwordResetLinkFailure = (error: unknown): PasswordResetLinkFailure | undefined => {
  const failure = authFailure(error);
  if (failure === 'network' || failure === 'too-many-requests' || failure === 'password-policy') {
    return undefined;
  }
  return (error as { code?: unknown } | null)?.code === 'auth/expired-action-code' ? 'expired' : 'invalid';
};

/**
 * Sends a reset email, rejecting only when the player can do something about it: too many requests or a network
 * failure. Anything else, such as an unknown address, resolves like a sent email, so the page never reveals whether
 * an account exists; it is logged by code only.
 */
export const sendResetEmailQuietly = async (
  passwordReset: PasswordReset,
  email: string,
  continueUrl: string,
): Promise<void> => {
  try {
    await passwordReset.sendResetEmail(email, { continueUrl });
  } catch (error) {
    const failure = authFailure(error);
    if (failure === 'network' || failure === 'too-many-requests') {
      throw error;
    }
    logger.error({ message: 'Password reset email refused', code: authErrorCode(error) });
  }
};
